import { UTC, DateRange, isNullOrUndefined, guid, TimestampRange, LQ, utc } from 'rewire-common';
import { observable, freeze } from 'rewire-core';
import { ICell, IColumn, IGrid, IGridOptions, IRow, IRowData, createGrid, createColumn, IColumnOptions } from 'rewire-grid';
import {
  numberColumn,
  ITimecard,
  ITemplate,
  IJob,
  IEmployeeSummary,
  BatchStatusType,
  ISimplifiedBatch,
  IMessage,
  IEmployeeTimecardStatus,
  IValidTransition,
  isMultiple,
  MultipleType,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import { formatTime, makeShortCellDate, localTimeZoneUTCNow } from '../utility/DateUtils';
import { nameMapper, codeMapper } from '../utility/DataConverters';
import SelectedRowWatcher from '../utility/SelectedRowWatcher';
import { BatchStatusRenderer, GroupTooltipCellRenderer, TimecardStatusIconRenderer } from './CellRenderers';
import { payRouteManager } from '../main-navigation/RouteManager';
import { DailyTimecardModelBase } from './DailyTimecardModel';
import { IRollupReason } from './RolledUpTimecard';
import timecardModel, { TimecardModelBase } from './TimecardModel';

export interface IRolledUpTimecard {
  id: string;
  status?: string;
  batchStatus?: BatchStatusType;
  batchName?: string;
  lastEdited?: string;
  earnedDate: UTC;
  start?: UTC;
  end?: UTC;
  units?: number;
  rate?: number | MultipleType;
  gross: number;
  accountCode?: string | MultipleType;
  template?: ITemplate | MultipleType;
  job?: IJob | MultipleType;
  otherPay?: number;
  reasons: IRollupReason[];
  timecards: ITimecard[];
  allowableTransitions: IValidTransition[];
  canEditTimecard: boolean;
  canTransitionTimecard: boolean;
  manualTimecard: boolean;
  isReversal: boolean;
  isCorrection: boolean;

  setBatch(newBatch: ISimplifiedBatch | undefined): void;
  setStatus(newStatus: IEmployeeTimecardStatus): void;
  update(timecards: ITimecard[], status: IEmployeeTimecardStatus | null): void;
}

interface IDailyRollupState {
  grid: IGrid;
  rolledUpTimecards: IRolledUpTimecard[];
  selectedRolledUpTimecard?: IRolledUpTimecard;
  isLoading: boolean;
}

export abstract class DailyRollupModelBase {
  state: IDailyRollupState;
  DYNAMIC_COLUMN_STARTING_POSITION: number = 12;
  selectedRowWatcher: SelectedRowWatcher = new SelectedRowWatcher();

  constructor() {
    this.state = observable({
      grid: {} as IGrid,
      rolledUpTimecards: [] as IRolledUpTimecard[],
      selectedRolledUpTimecard: undefined,
      isLoading: false,
    });
    this.grid = this.createGrid([]);
  }

  abstract get timecardModel(): TimecardModelBase;

  protected *getRange(): Iterable<UTC> {
    if (timecardModel.adjustments) {
      for (const earnedDate of LQ(timecardModel.timecards)
        .map((t) => t.earnedDate)
        .distinct((t) => t.utc)
        .toArray()) {
        yield utc(earnedDate);
      }
      return;
    }

    for (const earnedDate of this.effective!) {
      yield earnedDate;
    }
  }

  *getTimecards(): Iterable<IRolledUpTimecard> {
    for (const rut of this.state.rolledUpTimecards) {
      if (rut.isReversal) {
        continue;
      }
      yield rut;
    }
  }

  *getReversals(): Iterable<IRolledUpTimecard> {
    for (const rut of this.state.rolledUpTimecards) {
      if (rut.isReversal) {
        yield rut;
      }
    }
  }

  protected async setRolledUpTimecardsTemplates(rolledUpTimecards: IRolledUpTimecard[]) {
    const rolledUpTimecardsSingleTemplate = rolledUpTimecards.filter(
      (rut: IRolledUpTimecard) => rut.template && !isMultiple(rut.template),
    );
    const templates = await this.getTemplates(
      rolledUpTimecardsSingleTemplate.map((rut: IRolledUpTimecard) => (rut.template as ITemplate).id),
    );
    if (templates?.length) {
      for (const rut of rolledUpTimecardsSingleTemplate) {
        const matchingTemplate = templates!.find((t: ITemplate | undefined) => t && t.id === (rut.template as ITemplate).id);
        if (matchingTemplate) {
          (rut.template as ITemplate).template = matchingTemplate.template;
        }
      }
    }
  }

  protected async createRollups() {
    const rolledUpTimecards: IRolledUpTimecard[] = [];
    if (!this.effective || !this.selectedEmployee) return;

    for (const earnedDate of this.getRange()) {
      this.createReversalRollup(rolledUpTimecards, earnedDate);
      this.createRollup(rolledUpTimecards, earnedDate);
    }

    await this.setRolledUpTimecardsTemplates(rolledUpTimecards);
    this.rolledUpTimecards.set(rolledUpTimecards);
  }

  protected createRollup(rolledUpTimecards: IRolledUpTimecard[], earnedDate: UTC) {
    const timecards: ITimecard[] = this.timecardModel.timecards.filter((t) => earnedDate.equals(t.earnedDate));
    const statuses: IEmployeeTimecardStatus[] = this.timecardModel.statuses.filter((t) => earnedDate.equals(t.earnedDate));
    const status: IEmployeeTimecardStatus | null = statuses.length ? statuses[0] : null;
    rolledUpTimecards.push(this.timecardModel.createRollup(earnedDate, timecards, status));
  }

  protected createReversalRollup(rolledUpTimecards: IRolledUpTimecard[], earnedDate: UTC) {
    const statuses: IEmployeeTimecardStatus[] = this.timecardModel.statuses.filter((t) => earnedDate.equals(t.earnedDate));
    const status: IEmployeeTimecardStatus | null = statuses.length ? statuses[0] : null;
    const reversals: ITimecard[] = this.timecardModel.reversals.filter((t) => earnedDate.equals(t.earnedDate));
    if (reversals.length > 0) {
      rolledUpTimecards.push(this.timecardModel.createRollup(earnedDate, reversals, status));
    }
  }

  async updateRollup(rollupId: string) {
    const rollupRow = this.grid.row(rollupId);
    const rolledUpTimecard = this.rolledUpTimecards.find((rut: IRolledUpTimecard) => rut.id === rollupId);
    if (!rollupRow || !rolledUpTimecard) {
      return;
    }
    const earnedDate: UTC = rolledUpTimecard.earnedDate;
    const timecards: ITimecard[] = this.timecardModel.timecards.filter((t) => earnedDate.equals(t.earnedDate));
    const status: IEmployeeTimecardStatus = this.timecardModel.statuses.find((t) => earnedDate.equals(t.earnedDate))!;
    rolledUpTimecard.update(timecards, status);
    await this.setRolledUpTimecardsTemplates([rolledUpTimecard]);
    rollupRow.validate();
    rollupRow.mergeAllColumns();
  }

  reset() {
    freeze(() => {
      this.grid.set([]);
      this.rolledUpTimecards.length = 0;
      this.selectedRolledUpTimecard = undefined;
    });
  }

  async load(selectedEarnedDate?: UTC) {
    this.state.isLoading = true;
    try {
      await this.createRollups();

      const cols: IColumn[] = this.createColumns();
      const rows: IRowData[] = this.createRows(this.rolledUpTimecards, cols);
      const hasAdjustments: boolean = this.timecardModel.reversals.length > 0;
      if (hasAdjustments) {
        const adjustmentTotals: IRowData = this.createAdjustmentTotals();
        rows.push(adjustmentTotals);
      }
      const totals: IRowData = this.createTotals();
      rows.push(totals);

      const options: IGridOptions = this.createOptions();
      const grid: IGrid = createGrid(rows, cols, options);

      grid.rowHeight = 32;
      const adjustmentTotalsRow: any = hasAdjustments && grid.rows.length > 0 ? grid.rows[grid.rows.length - 2] : null;
      if (adjustmentTotalsRow) adjustmentTotalsRow.cls = 'totalRow reversal';
      const totalRow: any = grid.rows.length > 0 ? grid.rows[grid.rows.length - 1] : null;
      if (totalRow) totalRow.cls = 'totalRow';

      this.selectedRowWatcher.watch(grid, (row) => {
        this.selectedRolledUpTimecard = row;
      });
      this.grid = grid;

      this.identifyFutureDates(grid.rows);

      // re-select the row if it's possible
      if (selectedEarnedDate) {
        const selectedRow = grid.rows.find((r) => r.data?.earnedDate?.equals(selectedEarnedDate));
        if (selectedRow) {
          grid.selectRows([selectedRow]);
        }
      } else {
        if (rows.length === 1) {
          const selectedRow = grid.rows[0];
          if (selectedRow) {
            grid.selectRows([selectedRow]);
          }
        }
      }

      this.dailyTimecardModel.load();
    } catch (err) {
      this.reset();
    } finally {
      this.state.isLoading = false;
    }
  }

  protected identifyFutureDates(rows: IRow[]) {
    const todayEndOfDay = localTimeZoneUTCNow().endOfDay().add(14);
    const isInFuture = (date: UTC) => date > todayEndOfDay;

    for (const row of rows) {
      if (row.data.isReversal) {
        row.cls = 'reversal';
      }
      if (isInFuture(row.cells.earnedDate.value)) {
        for (const cellKey of Object.keys(row.cells)) {
          row.cells[cellKey].cls = 'future';
        }
      }
    }
  }

  async getTemplates(templateIds?: string[]): Promise<(ITemplate | undefined)[] | undefined> {
    if (!templateIds?.length) {
      return undefined;
    }
    const returnSchema = `
      id
      name
      template {
        start
        end
        units
        factor
        rate
        amount
        accountCode
        reason {
          id
          name
          category
        }
        overtimeReason {
          id
          name
        }
      }
    `;

    return (await DataQueries.getTemplatesByIds(returnSchema, templateIds)) as ITemplate[];
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  set grid(grid: IGrid) {
    this.state.grid = grid;
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  get rolledUpTimecards(): IRolledUpTimecard[] {
    return this.state.rolledUpTimecards;
  }

  get selectedRolledUpTimecard(): IRolledUpTimecard | undefined {
    return this.state.selectedRolledUpTimecard;
  }

  set selectedRolledUpTimecard(timecard: IRolledUpTimecard | undefined) {
    this.state.selectedRolledUpTimecard = timecard;
  }

  get dailyTimecardModel(): DailyTimecardModelBase {
    return this.timecardModel.dailyTimecardModel;
  }

  get editTimecard() {
    return this.dailyTimecardModel.editTimecard;
  }

  get effective(): DateRange | undefined {
    return this.timecardModel.effective;
  }

  get selectedEmployee(): IEmployeeSummary | undefined {
    return this.timecardModel.selectedEmployee;
  }

  get selectedRows(): IRow[] {
    return this.grid.selectedRows || [];
  }

  get viewOnly(): boolean {
    return this.timecardModel.viewOnly;
  }

  isNewTimecard(timecard: ITimecard): boolean {
    if (!this.selectedRolledUpTimecard?.timecards.length) return false;

    // the new timecard should be the last one in the list for the current selected rolled up timecard
    return this.selectedRolledUpTimecard.timecards[this.selectedRolledUpTimecard.timecards.length - 1].id === timecard.id;
  }

  select(timecard: IRolledUpTimecard) {
    const rolledUpTimecard = this.grid.rows.find((t) => t.cells.earnedDate.value.equals(timecard.earnedDate));
    if (rolledUpTimecard) {
      this.grid.selectedRows.push(rolledUpTimecard);
    }
  }

  get dailyTimecards(): ITimecard[] {
    const selectedRolledUpTimecard = this.selectedRolledUpTimecard;
    if (!selectedRolledUpTimecard?.earnedDate) return [];

    return selectedRolledUpTimecard.timecards;
  }

  cancel() {
    this.grid.revert();
  }

  handleMessageSelected(message: IMessage) {
    const row = this.grid.rows.find((r) => r.data.earnedDate.equals(message.effective));
    if (row) {
      this.grid.selectRows([row]);
    }
  }

  protected get columns(): any[] {
    return [
      { name: 'status', label: '', type: 'none', width: '35px', renderer: TimecardStatusIconRenderer },
      { name: 'batchStatus', label: '', type: 'none', width: '35px', renderer: BatchStatusRenderer },
      { name: 'earnedDate', label: 'Date', type: 'none', map: makeShortCellDate, width: '70px' },
      { name: 'group', label: 'Group', type: 'none', map: nameMapper, renderer: GroupTooltipCellRenderer },
      { name: 'template', label: 'Shift Type', type: 'none', map: codeMapper },
      { name: 'start', label: 'Start Time', type: 'none', map: formatTime, options: { disableErrors: false } },
      { name: 'end', label: 'End Time', type: 'none', map: formatTime, options: { disableErrors: false } },
      { name: 'units', label: 'Units', type: numberColumn },
      { name: 'reason', label: 'Reason', type: 'none', map: nameMapper },
      { name: 'occupation', label: 'Occupation', type: 'none', map: nameMapper },
      { name: 'rate', label: 'Rate', type: 'none', align: 'right' },
      { name: 'accountCode', label: 'Account Code', type: 'none' },
      { name: 'gross', label: 'Gross', type: numberColumn },
      { name: 'otherPay', label: 'Other Pay', type: numberColumn },
    ];
  }

  protected get reasonColumnNames(): string[] {
    const reasons: IRollupReason[] = [];

    for (const timecard of this.rolledUpTimecards) {
      for (const reason of timecard.reasons) {
        if (!reasons.map((r) => r.name).includes(reason.name)) {
          reasons.push(reason);
        }
      }
    }
    return reasons.sort(this.orderByCategory).map((r) => r.name);
  }

  protected orderByCategory(reason1: IRollupReason, reason2: IRollupReason): number {
    // if categories match, order by name

    if (reason1.columnOrder === reason2.columnOrder) {
      return reason1.name < reason2.name ? -1 : 1;
    }
    return reason1.columnOrder - reason2.columnOrder;
  }

  protected createGrid(results: IRolledUpTimecard[] | undefined): IGrid {
    const cols: IColumn[] = this.createColumns();
    const rows: IRowData[] = this.createRows(results, cols);
    const options: IGridOptions = this.createOptions();
    const grid: IGrid = createGrid(rows, cols, options);
    grid.rowHeight = 32;
    return grid;
  }

  protected createOptions(): IGridOptions {
    return {
      readOnly: true,
      clearSelectionOnBlur: false,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+V': async (evt: React.KeyboardEvent<any>, _cell: ICell) => {
          if (this.viewOnly) return;
          evt.stopPropagation();
          evt.preventDefault();

          if (this.selectedRows.length <= 0) return;

          const clipboard = (this.grid as any).clipboard;
          if (clipboard.length <= 0) return;

          const clipboardRow = this.grid.rowByPos(clipboard[0].rowPosition);
          if (!clipboardRow || isNullOrUndefined(clipboardRow.data.earnedDate) || !clipboardRow.data.timecards?.length) {
            return;
          }

          const clipboardTemplate = clipboardRow.data.template; // Fixed typo

          try {
            // Use Promise.all to handle all async operations
            await Promise.all(
              this.selectedRows.map(async (selectedRow) => {
                const earnedDate = selectedRow.data.earnedDate;
                if (isNullOrUndefined(earnedDate)) {
                  return;
                }

                const timecardsToCopy = clipboardRow.data.timecards.map((t: any) => {
                  let effective = t.effective;
                  if (effective) {
                    const dayDiff = earnedDate.clone().startOfDay().subtractDate(t.earnedDate.clone().startOfDay());
                    const start = effective.start.add(dayDiff);
                    const end = effective.end.add(dayDiff);
                    effective = new TimestampRange(start, end);
                  }
                  return Object.assign({}, t, {
                    id: guid(),
                    earnedDate: earnedDate,
                    effective: effective,
                    start: effective.start,
                    end: effective.end,
                  });
                });

                await this.editTimecard.load(
                  timecardsToCopy.find((t: any) => t.reason?.category === 'worked') ?? timecardsToCopy[0],
                  clipboardTemplate,
                  timecardsToCopy,
                  true,
                );
                await this.editTimecard.emit('edit', undefined, earnedDate);
              }),
            );
          } catch (error) {
            console.error('Error while pasting timecards:', error);
            throw error;
          }

          (this.grid as any).clipboard = clipboard;
        },
      },
    };
  }

  protected createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    for (const col of this.columns) {
      const { name, label, ...opts } = col;
      opts.canSort = false;
      const column = createColumn(name, label, opts);
      if (opts?.options) column.typeOptions = opts.options;
      cols.push(column);
    }

    let index = 0;
    for (const col of this.reasonColumnNames) {
      const opts = { type: numberColumn };
      const column = createColumn(col, col, opts as IColumnOptions);
      column.align = 'right';
      column.canSort = false;
      column.typeOptions = numberColumn.options;
      cols.splice(index + this.DYNAMIC_COLUMN_STARTING_POSITION, 0, column);
      index++;
    }
    return cols;
  }

  protected createRows(rowData: IRolledUpTimecard[] | undefined, cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (rowData) {
      for (const row of rowData) {
        const r: IRowData = { id: `${row.id}`, data: row };
        for (const col of cols) {
          const fieldName = col.name;
          const reasonData = row.reasons.find((reason: IRollupReason) => reason.name === fieldName);
          if (reasonData) {
            r.data![fieldName] = reasonData.units;
          }
        }
        rows.push(r);
      }
    }
    return rows;
  }

  protected createTotals(): IRowData {
    const initialRow: IRowData = {
      id: 'totalRow',
      data: {
        units: 0,
        gross: 0,
        otherPay: 0,
      },
    };

    return Array.from(this.getTimecards()).reduce((prev: IRowData, curr: IRolledUpTimecard) => {
      prev.data!.units += curr.units ? curr.units : 0;
      prev.data!.gross += curr.gross ? curr.gross : 0;
      prev.data!.otherPay += curr.gross ? curr.otherPay : 0;
      for (const reason of curr.reasons) {
        if (!prev.data![reason.name]) {
          prev.data![reason.name] = reason.units;
        } else {
          prev.data![reason.name] += reason.units;
        }
      }
      return prev;
    }, initialRow);
  }

  protected createAdjustmentTotals(): IRowData {
    const initialRow: IRowData = {
      id: 'adjustmentsTotalRow',
      data: {
        units: 0,
        gross: 0,
        otherPay: 0,
      },
    };

    return Array.from(this.getReversals()).reduce((prev: IRowData, curr: IRolledUpTimecard) => {
      prev.data!.units += curr.units ? curr.units : 0;
      prev.data!.gross += curr.gross ? curr.gross : 0;
      prev.data!.otherPay += curr.gross ? curr.otherPay : 0;
      for (const reason of curr.reasons) {
        if (!prev.data![reason.name]) {
          prev.data![reason.name] = reason.units;
        } else {
          prev.data![reason.name] += reason.units;
        }
      }
      return prev;
    }, initialRow);
  }
}

export class DailyRollupModel extends DailyRollupModelBase {
  constructor() {
    super();
  }

  get timecardModel(): TimecardModelBase {
    return timecardModel;
  }

  get isLoading(): boolean {
    return this.state.isLoading || payRouteManager.isLoading;
  }
}

const dailyRollupModel = new DailyRollupModel();
export default dailyRollupModel;
